import { IAttribute, IModel, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { Activity } from './activity';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { ModelConstants } from '../model-constants';
import { format, stringIsNullOrEmpty } from '../../shared/utils/string.util';
import { TridentSparkJobDefinitionLinkedService } from '../linkedservice/trident-sparkjobdefinition.linkedservice';
import { ParameterValue } from '../base/parameter-value';
import { IFabricArtifactSelectValue } from '../trident.backendmodel';
import { LinkedService } from '../linkedservice/linkedservice';
import { Copier } from '../base/copier';
import { GlobalVariables } from '../../constants/globals';
import { ParameterConfig } from './parameter-config';

@Metamodel.model({
    name: ModelConstants.tridentSparkJobDefinitionActivity,
    referable: true
})
export class TridentSparkJobDefinitionActivity extends Activity {

    public static readonly MODEL: IModel;
    public static readonly SPARKJOBDEFINITIONID: IAttribute;
    public static readonly WORKSPACEID: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly EXECUTABLEFILE: IAttribute;
    public static readonly MAINCLASS: IAttribute;
    public static readonly ADDITIONALLIBRARYURIS: IAttribute;
    public static readonly COMMANDLINEARGUMENTS: IAttribute;
    public static readonly DEFAULTLAKEHOUSEWID: IAttribute;
    public static readonly DEFAULTLAKEHOUSEAID: IAttribute;
    public static readonly ADDITIONALLAKEHOUSES: IAttribute;
    public static readonly ENVIRONMENTID: IAttribute;
    public static readonly ENVIRONMENTWID: IAttribute;
    public static readonly ENVIRONMENTAID: IAttribute;
    public static readonly SPARK_DRIVER_CORE: IAttribute;
    public static readonly SPARK_DRIVER_MEMORY: IAttribute;
    public static readonly SPARK_EXECUTOR_CORE: IAttribute;
    public static readonly SPARK_EXECUTOR_MEMORY: IAttribute;
    public static readonly DYNAMIC_ALLOCATION: IAttribute;
    public static readonly SPARK_EXECUTOR_INSTANCES: IAttribute;
    public static readonly MIN_EXECUTORS: IAttribute;
    public static readonly MAX_EXECUTORS: IAttribute;
    public static readonly SPARK_PROPERTIES: IAttribute;

    @Metamodel.attribute({
        display: shellStringResources.ConnectionLabel,
        reference: true,
        type: TridentSparkJobDefinitionLinkedService.MODEL,
        constraint: {
            validator: TridentSparkJobDefinitionActivity.validateConnection
        }
    })
    public tridentSparkJobDefinitionLinkedService?: TridentSparkJobDefinitionLinkedService;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    @Metamodel.attribute({
        display: shellStringResources.TridentSparkJobDefinitionActivityIdLabel,
        association: 'SPARKJOBDEFINITIONID',
        constraint: {
            validator: TridentSparkJobDefinitionActivity.validateSparkJobDefinitionId
        },
        templatable: true
    })
    public sparkJobDefinitionId?: string;

    @Metamodel.attribute({
        display: shellStringResources.TridentWorkspaceIdLabel,
        association: 'WORKSPACEID',
        constraint: {
            validator: TridentSparkJobDefinitionActivity.validateWorkspaceId
        },
        templatable: true
    })
    public workspaceId?: string;

    @Metamodel.attribute({
        association: 'EXECUTABLEFILE',
        accepts: ['String'],
        templatable: true
    })
    public executableFile?: string;

    @Metamodel.attribute({
        association: 'MAINCLASS',
        accepts: ['String'],
        templatable: true
    })
    public mainClass?: string;

    @Metamodel.attribute({
        association: 'ADDITIONALLIBRARYURIS',
        accepts: ['String'],
        templatable: true
    })
    public additionalLibraryUris?: string;

    @Metamodel.attribute({
        association: 'COMMANDLINEARGUMENTS',
        accepts: ['String'],
        templatable: true
    })
    public commandLineArguments?: string;

    @Metamodel.attribute({
        association: 'DEFAULTLAKEHOUSEWID',
        accepts: ['String'],
        templatable: true,
        constraint: {
            validator: TridentSparkJobDefinitionActivity.validateDefaultLakehouseWorkspace
        }
    })
    public defaultLakehouseWorkspaceId?: string;

    @Metamodel.attribute({
        association: 'DEFAULTLAKEHOUSEAID',
        accepts: ['String'],
        templatable: true,
        constraint: {
            validator: TridentSparkJobDefinitionActivity.validateDefaultLakehouseArtifact
        }
    })
    public defaultLakehouseArtifactId?: string;

    @Metamodel.attribute({
        association: 'ADDITIONALLAKEHOUSES',
        accepts: ['Array'],
        templatable: true,
        constraint: {
            validator: TridentSparkJobDefinitionActivity.validateAdditionalLakehouses
        }
    })
    public additionalLakehouses?: IFabricArtifactSelectValue[] = [];

    @Metamodel.attribute({
        association: 'ENVIRONMENTID',
        accepts: ['String'],
        templatable: true
    })
    public environmentId?: string;

    @Metamodel.attribute({
        association: 'ENVIRONMENTWID',
        accepts: ['String'],
        templatable: true,
        constraint: {
            validator: TridentSparkJobDefinitionActivity.validateEnvironmentWorkspace
        }
    })
    public environmentWorkspaceId?: string;

    @Metamodel.attribute({
        association: 'ENVIRONMENTAID',
        accepts: ['String'],
        templatable: true,
        constraint: {
            validator: TridentSparkJobDefinitionActivity.validateEnvironmentArtifact
        }
    })
    public environmentArtifactId?: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesDriverCores,
        association: 'SPARK_DRIVER_CORE',
        templatable: true
    })
    public sparkDriverCore?: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesDriverMemory,
        association: 'SPARK_DRIVER_MEMORY',
        templatable: true
    })
    public sparkDriverMemory?: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesExecutorCores,
        association: 'SPARK_EXECUTOR_CORE',
        templatable: true
    })
    public sparkExecutorCore?: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesExecutorMemory,
        association: 'SPARK_EXECUTOR_MEMORY',
        templatable: true
    })
    public sparkExecutorMemory?: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityDynamicExecutorLabel,
        association: 'DYNAMIC_ALLOCATION',
        templatable: true
    })
    public dynamicAllocation?: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesNumExecutors,
        association: 'SPARK_EXECUTOR_INSTANCES',
        templatable: true
    })
    public sparkExecutorInstances?: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityMinExecutorsLabel,
        association: 'MIN_EXECUTORS',
        templatable: true
    })
    public minExecutors?: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityMaxExecutorsLabel,
        association: 'MAX_EXECUTORS',
        templatable: true
    })
    public maxExecutors?: number;

    @Metamodel.attribute({
        display: shellStringResources.TridentSparkPropertiesLabel,
        association: 'SPARK_PROPERTIES'
    })
    public sparkProperties?: ParameterConfig[] = [];

    public isConnectionRequired: boolean = false;

    private static validateWorkspaceId(_tridentSparkJobDefinitionActivity?: TridentSparkJobDefinitionActivity, workspaceId?: string): IViolation[] | undefined {
        if (!workspaceId) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.TridentWorkspaceIdLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateSparkJobDefinitionId(_tridentSparkJobDefinitionActivity?: TridentSparkJobDefinitionActivity, sparkJobDefinitionId?: string): IViolation[] | undefined {
        if (!sparkJobDefinitionId) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.TridentSparkJobDefinitionActivityIdLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateDefaultLakehouseWorkspace(tridentSparkJobDefinitionActivity?: TridentSparkJobDefinitionActivity, _str?: string): IViolation[] | undefined {
        const isWorkspaceConfigured = !stringIsNullOrEmpty(tridentSparkJobDefinitionActivity.defaultLakehouseWorkspaceId) ||
            TridentSparkJobDefinitionActivity.DEFAULTLAKEHOUSEWID.templateValue(tridentSparkJobDefinitionActivity) != null;
        const isArtifactConfigured = !stringIsNullOrEmpty(tridentSparkJobDefinitionActivity.defaultLakehouseArtifactId) ||
            TridentSparkJobDefinitionActivity.DEFAULTLAKEHOUSEAID.templateValue(tridentSparkJobDefinitionActivity) != null;
        if (isArtifactConfigured && !isWorkspaceConfigured) {
            return [
                {
                    message: () =>
                        format(
                            shellStringResources.TridentSparkJobDefinitionReferenceValidationError,
                            shellStringResources.TridentSparkJobDefinitionDefaultLakehouseReferenceLabel,
                            shellStringResources.TridentSparkJobDefinitionLakehouseLabel
                        )
                }
            ];
        }
        return undefined;
    }

    private static validateDefaultLakehouseArtifact(tridentSparkJobDefinitionActivity?: TridentSparkJobDefinitionActivity, _str?: string): IViolation[] | undefined {
        const isWorkspaceConfigured = !stringIsNullOrEmpty(tridentSparkJobDefinitionActivity.defaultLakehouseWorkspaceId) ||
            TridentSparkJobDefinitionActivity.DEFAULTLAKEHOUSEWID.templateValue(tridentSparkJobDefinitionActivity) != null;
        const isArtifactConfigured = !stringIsNullOrEmpty(tridentSparkJobDefinitionActivity.defaultLakehouseArtifactId) ||
            TridentSparkJobDefinitionActivity.DEFAULTLAKEHOUSEAID.templateValue(tridentSparkJobDefinitionActivity) != null;
        if (isWorkspaceConfigured && !isArtifactConfigured) {
            return [
                {
                    message: () =>
                        format(
                            shellStringResources.TridentSparkJobDefinitionReferenceValidationError,
                            shellStringResources.TridentSparkJobDefinitionDefaultLakehouseReferenceLabel,
                            shellStringResources.TridentSparkJobDefinitionLakehouseLabel
                        )
                }
            ];
        }
        return undefined;
    }

    private static validateAdditionalLakehouses(tridentSparkJobDefinitionActivity?: TridentSparkJobDefinitionActivity, _list?: IFabricArtifactSelectValue[]): IViolation[] | undefined {
        if (TridentSparkJobDefinitionActivity.ADDITIONALLAKEHOUSES.templateValue(tridentSparkJobDefinitionActivity) != null) {
            return undefined;
        }
        if (tridentSparkJobDefinitionActivity.additionalLakehouses?.length > 0) {
            for (const lakehouse of tridentSparkJobDefinitionActivity.additionalLakehouses) {
                if (stringIsNullOrEmpty(lakehouse.workspaceId) || stringIsNullOrEmpty(lakehouse.artifactId)) {
                    return [
                        {
                            message: () =>
                                format(
                                    shellStringResources.TridentSparkJobDefinitionReferenceValidationError,
                                    shellStringResources.TridentSparkJobDefinitionAdditionalLakehouseReferenceLabel,
                                    shellStringResources.TridentSparkJobDefinitionAdditionalLakehouseLabel
                                )
                        }
                    ];
                }
            }
        }
        return undefined;
    }

    private static validateConnection(_activity?: TridentSparkJobDefinitionActivity, linkedService?: LinkedService): IViolation[] | undefined {
        if (_activity.isConnectionRequired && !linkedService) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.ConnectionLabel)
                }
            ];
        } else if (linkedService && linkedService.getLinkedServiceType() != Metamodel.modelFor(TridentSparkJobDefinitionLinkedService).name) {
            return [
                {
                    message: () =>
                        format(
                            shellStringResources.ActivityNotSupportLinkedServiceError,
                            format(shellStringResources.ModelName_ActivityTemplate, shellStringResources.SparkJobDefinitionActivityLabel),
                            format(shellStringResources.ModelName_ConnectionTemplate, Metamodel.modelOf(linkedService).name)
                        ),
                    messageType: 'unsupported'
                }
            ];
        }

        return undefined;
    }

    private static validateEnvironmentWorkspace(tridentSparkJobDefinitionActivity?: TridentSparkJobDefinitionActivity, _str?: string): IViolation[] | undefined {
        const isWorkspaceConfigured = !stringIsNullOrEmpty(tridentSparkJobDefinitionActivity.environmentWorkspaceId) ||
            TridentSparkJobDefinitionActivity.ENVIRONMENTWID.templateValue(tridentSparkJobDefinitionActivity) != null;
        const isArtifactConfigured = !stringIsNullOrEmpty(tridentSparkJobDefinitionActivity.environmentArtifactId) ||
            TridentSparkJobDefinitionActivity.ENVIRONMENTAID.templateValue(tridentSparkJobDefinitionActivity) != null;
        if (isArtifactConfigured && !isWorkspaceConfigured) {
            return [
                {
                    message: () =>
                        format(
                            shellStringResources.TridentSparkJobDefinitionReferenceValidationError,
                            shellStringResources.TridentSparkJobDefinitionEnvironmentReferenceLabel,
                            shellStringResources.TridentSparkJobDefinitionEnvironmentLabel
                        )
                }
            ];
        }
        return undefined;
    }

    private static validateEnvironmentArtifact(tridentSparkJobDefinitionActivity?: TridentSparkJobDefinitionActivity, _str?: string): IViolation[] | undefined {
        const isWorkspaceConfigured = !stringIsNullOrEmpty(tridentSparkJobDefinitionActivity.environmentWorkspaceId) ||
            TridentSparkJobDefinitionActivity.ENVIRONMENTWID.templateValue(tridentSparkJobDefinitionActivity) != null;
        const isArtifactConfigured = !stringIsNullOrEmpty(tridentSparkJobDefinitionActivity.environmentArtifactId) ||
            TridentSparkJobDefinitionActivity.ENVIRONMENTAID.templateValue(tridentSparkJobDefinitionActivity) != null;
        if (isWorkspaceConfigured && !isArtifactConfigured) {
            return [
                {
                    message: () =>
                        format(
                            shellStringResources.TridentSparkJobDefinitionReferenceValidationError,
                            shellStringResources.TridentSparkJobDefinitionEnvironmentReferenceLabel,
                            shellStringResources.TridentSparkJobDefinitionEnvironmentLabel
                        )
                }
            ];
        }
        return undefined;
    }

    public onActivityCloned(clonedActivity: TridentSparkJobDefinitionActivity, context: { inlineDataset?: boolean }): void {
        if (GlobalVariables.isTrident) {
            if (this.tridentSparkJobDefinitionLinkedService?.parameterized) {
                clonedActivity.tridentSparkJobDefinitionLinkedService = Copier.clone(
                    context,
                    [this.tridentSparkJobDefinitionLinkedService],
                    undefined,
                    true
                )[0] as TridentSparkJobDefinitionLinkedService;
            }
        }
    }
}
